import { AlertTriangle, Loader2, SearchX, XCircle } from "lucide-react";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import type { KeywordBankRun } from "@/types";
import { landscapeRunElapsedLabel } from "@/utils/landscapeRunStatus";

/**
 * `components/keyword.md § The Keyword Bank screen`, `WEB-004`. A
 * presentational banner for a single hero product's keyword bank run.
 *
 * The prop is widened to `KeywordBankRun | null | undefined` on purpose,
 * and `undefined` is not `null`: `useGetKeywordBankRun` yields
 * `data === undefined` on every first render and again on any settled
 * error (`apiService` throws and `query-client.ts` retries only
 * 5xx/network before settling `isError` with `data: undefined`). A branch
 * keyed on `run === null` alone would be `false` for `undefined`, so
 * `run.status` would throw and white-screen this tab --
 * `strictNullChecks: false` catches none of it. Every branch below is
 * written against null-ish, not against `null`.
 *
 * Five branches, and nothing else:
 * - `queued` / `running` -- being built
 * - `stopped` -- NFR-1's derivation, reachable even though FR-6 only
 *   enumerates four cases; rendering it as *being built* would be a lie
 *   hours old, rendering it as *failed* would claim an ending the product
 *   never observed
 * - `succeeded_without_keywords` -- finished, and the sub-category had
 *   nothing. The ~29% case, and it is the correct answer, not a failure
 * - `failed` -- the run's `error_detail`
 * - `succeeded_with_keywords`, `null`, `undefined`, or any unrecognised
 *   status -- nothing. The table (or the empty state) is the answer, and
 *   an unrecognised status renders nothing rather than throwing.
 */
export function KeywordBankBuildState({ run }: { run: KeywordBankRun | null | undefined }) {
  if (run === null || run === undefined) return null;

  if (run.status === "queued" || run.status === "running") {
    return (
      <Alert variant="info">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        <AlertTitle>Building your keyword bank</AlertTitle>
        <AlertDescription>
          We&apos;re filling this product&apos;s keyword bank from its sub-category. There&apos;s
          nothing to do here yet -- this will update on its own once it&apos;s done.
        </AlertDescription>
      </Alert>
    );
  }

  if (run.status === "stopped") {
    const elapsed = landscapeRunElapsedLabel(
      run.status,
      run.started_at,
      run.ended_at,
      run.state_changed_at,
      run.requested_at,
      new Date(),
    );
    return (
      <Alert>
        <AlertTriangle className="h-4 w-4" aria-hidden="true" />
        <AlertTitle>Stopped</AlertTitle>
        <AlertDescription>
          No progress for {elapsed}. If work resumes, this will pick back up on its own.
        </AlertDescription>
      </Alert>
    );
  }

  if (run.status === "succeeded_without_keywords") {
    return (
      <Alert>
        <SearchX className="h-4 w-4" aria-hidden="true" />
        <AlertTitle>Finished -- the sub-category had no keywords</AlertTitle>
        <AlertDescription>
          The build completed and found nothing that met the bar for this sub-category. That is a
          correct outcome, not a broken one -- add your own keywords below to get started.
        </AlertDescription>
      </Alert>
    );
  }

  if (run.status === "failed") {
    return (
      <Alert variant="destructive">
        <XCircle className="h-4 w-4" aria-hidden="true" />
        <AlertTitle>Keyword bank build failed</AlertTitle>
        <AlertDescription>{run.error_detail}</AlertDescription>
      </Alert>
    );
  }

  // succeeded_with_keywords: the table is the answer. Any other,
  // unrecognised status string renders nothing rather than throwing.
  return null;
}

/**
 * The empty-state rule, as a pure function -- `KeywordBankContentV2` adds
 * no condition of its own (`components/keyword.md § The Keyword Bank
 * screen`, rule 2). `true` only when the bank is empty **and** the run is
 * null-ish or `succeeded_with_keywords` -- FR-6's "never show the fourth
 * for any of the first three", and the `undefined` case is why the
 * existing "No keywords yet" state keeps rendering on a persistent 500
 * from the new endpoint instead of disappearing.
 */
export function shouldShowKeywordBankEmptyState(
  run: KeywordBankRun | null | undefined,
  isBankEmpty: boolean,
): boolean {
  if (!isBankEmpty) return false;
  if (run === null || run === undefined) return true;
  return run.status === "succeeded_with_keywords";
}
